<template>
  <section class="dealer-contacts">
    <!-- Background image -->
    <div class="dealer-contacts__background">
      <!-- <client-only>
        <store-map :place="place" />
        <template #fallback>
          <div class="dealer-contacts__map-fallback">
            <div class="loading-placeholder">
              Loading map...
            </div>
          </div>
        </template>
      </client-only> -->
      <static-map
        :place="place"
        :maps-url="mapsUrl" />
    </div>

    <!-- Content overlay -->
    <div class="dealer-contacts__container">
      <div class="dealer-contacts__content">
        <!-- Title -->
        <h2 class="dealer-contacts__title">
          {{ title }}
        </h2>

        <!-- Contact information -->
        <div class="dealer-contacts__info">
          <p class="dealer-contacts__text">
            {{ contactInfo }}
          </p>
        </div>

        <!-- FAB buttons -->
        <div
          v-if="hasPhone || hasWhatsapp"
          class="dealer-contacts__actions">
          <fab-button
            v-if="hasPhone"
            variant="phone"
            :href="phoneHref"
            :aria-label="$t('dealer.accessibility.callDealer')"
            @click="$emit('phone-click')" />

          <fab-button
            v-if="hasWhatsapp"
            variant="whatsapp"
            :href="whatsappHref"
            :aria-label="$t('dealer.accessibility.contactWhatsapp')"
            @click="$emit('whatsapp-click')" />
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
  import { computed } from 'vue'

  import FabButton from '../FabButton.vue'

  // Props
  const props = defineProps({
    title: {
      type: String,
      required: true
    },
    contactInfo: {
      type: String,
      required: true,
      validator: value => typeof value === 'string' && value.trim().length > 0
    },
    phone: {
      type: String,
      default: null
    },
    whatsapp: {
      type: String,
      default: null
    },
    mapsUrl: {
      type: String,
      default: null
    },
    place: {
      type: Object,
      default: null
    },
    phoneHref: {
      type: String,
      default: null
    },
    whatsappHref: {
      type: String,
      default: null
    }
  })

  // Computed properties
  const hasPhone = computed(() => !!(props.phone && props.phone.trim().length > 0))
  const hasWhatsapp = computed(() => !!(props.whatsapp && props.whatsapp.trim().length > 0))

  // Emits
  defineEmits([ 'phone-click', 'whatsapp-click' ])
</script>

<style lang="scss" scoped>
:deep(.store-map) {
  border-radius: 0 !important;

  // width: 60vw;
}

.dealer-contacts {
  width: 100%;

  // height: 100dvh; // Fixed height from design
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: var(--space-n);
  padding: rem(40px) 0;

  @include mq(medium) {
    display: grid;
    grid-template-columns: minmax(0, 60%) minmax(0, 40%);
    gap: 0;
  }

  // Background takes all remaining space
  &__background {
    flex: 1;
    height: 100%;
    :deep(img) { object-fit: cover; }

    // background-color: var(--color-black);
  }

  // Content container (right side) takes only necessary space
  &__container {
    display: flex;
    align-items: center;
    padding: 0 rem(32px);
  }

  // Content wrapper
  &__content {
    display: flex;
    flex-direction: column;

    // align-items: center;
    text-align: center;
  }

  // Title styling
  &__title {
    color: var(--color-black);
    font-family: var(--font-family-poppins);
    font-size: var(--typography-size-l);
    font-weight: var(--font-weight-medium);
    line-height: 1.4;
    margin: 0;
    text-align: center;
  }

  // Contact info container
  &__info {
    margin-top: var(--space-n);
    width: 100%;
  }

  // Contact text styling
  &__text {
    color: var(--color-black);
    font-family: var(--font-family-poppins);
    font-size: 16px; // From Figma design
    font-weight: var(--font-weight-normal);
    line-height: 1.5;
    margin: 0;
    text-align: center;
    white-space: pre-line; // Allow line breaks in text
  }

  // FAB buttons container
  &__actions {
    display: flex;
    align-items: center;
    gap: var(--space-xxs); // 10px gap to match footer
    justify-content: center;
    margin-top: rem(32px);

    @include mq(medium) {
      margin-top: var(--space-xxl);
    }
  }
}

.dealer-contacts__map-fallback,
.loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  background: var(--color-light-grey, #f5f5f5);
  border-radius: var(--space-xs, 8px);
  color: var(--color-dark-grey, #666);
  font-size: var(--typography-size-ui-kit-14, 14px);
}
</style>
